export const STUDIO_FEATURED_OUTCOME_SOURCE = 'studio-featured-outcome' as const
export const STUDIO_NEWSLETTER_MEMBERSHIP_SKU_ID =
  'newsletter-membership.2026-07' as const
export const STUDIO_RESERVATION_OPERATIONS_SKU_ID =
  'reservation-operations.2026-07' as const
export const STUDIO_NEWSLETTER_MEMBERSHIP_GENERATION_GOLDEN_SKU_ID =
  'newsletter-membership.v1' as const
export const STUDIO_RESERVATION_OPERATIONS_GENERATION_GOLDEN_SKU_ID =
  'reservation-operations.2026-07' as const

const NEWSLETTER_MEMBERSHIP_PROVENANCE_SUBJECTS = [
  'https://cogo.xyz/instances/example/golden-sku-newsletter-membership-action-applyOperatorChange',
  'https://cogo.xyz/instances/example/agentic-app-process-cross-page-app-process',
  'https://cogo.xyz/instances/example/golden-sku-newsletter-membership-action-openMemberContent',
  'https://cogo.xyz/instances/example/golden-sku-newsletter-membership-contract-scenarios',
  'https://cogo.xyz/instances/example/agentic-publish-readiness-cross-page-app-process',
  'https://cogo.xyz/instances/example/agentic-publish-readiness-csv-export',
] as const

const NEWSLETTER_MEMBERSHIP_ADAPTIVE_RUNTIME = {
  schemaVersion: 'studio-adaptive-runtime-plan.v1',
  enabled: true,
  situationSignals: [
    'device-runtime',
    'evidence-quality',
    'user-context',
    'business-state',
  ],
  adaptationAxes: [
    'layout-density',
    'renderer-target',
    'design-style',
    'workflow',
    'action-availability',
    'data-scope',
  ],
  localStatePolicy: 'block-local-first',
  globalStatePolicy: 'appData-shared',
  evidencePolicy: {
    requireMeasuredDesignEvidence: true,
    quarantineUnmeasuredOntologyFacts: true,
    requireActionFlowTraceForDynamicApps: true,
  },
  notes: [
    'Adaptation must preserve block-local state boundaries before writing shared appData.',
    'Ontology promotion remains blocked until rendered design evidence is measured.',
    'Dynamic apps must prove ActionFlow traces before adaptive facts are promoted.',
  ],
} as const

export const STUDIO_OUTCOME_CATALOG = [
  {
    skuId: STUDIO_NEWSLETTER_MEMBERSHIP_SKU_ID,
    generationGoldenSkuId:
      STUDIO_NEWSLETTER_MEMBERSHIP_GENERATION_GOLDEN_SKU_ID,
    copyKey: 'newsletterMembership',
  },
  {
    skuId: STUDIO_RESERVATION_OPERATIONS_SKU_ID,
    generationGoldenSkuId:
      STUDIO_RESERVATION_OPERATIONS_GENERATION_GOLDEN_SKU_ID,
    copyKey: 'reservationOperations',
  },
] as const

export type StudioOutcomeCatalogEntry = (typeof STUDIO_OUTCOME_CATALOG)[number]
export type StudioOutcomeSkuId = StudioOutcomeCatalogEntry['skuId']
export type StudioOutcomeCopyKey = StudioOutcomeCatalogEntry['copyKey']
export type StudioOutcomeGenerationGoldenSkuId =
  StudioOutcomeCatalogEntry['generationGoldenSkuId']

export interface StudioOutcomeGenerationContract {
  goldenSkuId: StudioOutcomeGenerationGoldenSkuId
  pages: Array<{ id: string; route: string; title: string; blocks: string[] }>
  blocks: Array<{ name: string; type: string }>
  globalState: Record<string, { type: string; default: unknown }>
}

export interface StudioOutcomeGenerationAuthorityInput {
  appSpec: unknown
  pages: unknown
  blocks: unknown
}

export interface StudioOutcomeIntent {
  source: typeof STUDIO_FEATURED_OUTCOME_SOURCE
  skuId: StudioOutcomeSkuId
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function exactKeys(
  value: Record<string, unknown>,
  expected: string[],
): boolean {
  return (
    Object.keys(value).sort().join('\0') === [...expected].sort().join('\0')
  )
}

function exactJsonValue(actual: unknown, expected: unknown): boolean {
  if (Array.isArray(expected)) {
    return (
      Array.isArray(actual) &&
      actual.length === expected.length &&
      expected.every((item, index) => exactJsonValue(actual[index], item))
    )
  }
  const expectedRecord = record(expected)
  if (expectedRecord) {
    const actualRecord = record(actual)
    if (
      !actualRecord ||
      !exactKeys(actualRecord, Object.keys(expectedRecord))
    ) {
      return false
    }
    return Object.entries(expectedRecord).every(([key, value]) =>
      exactJsonValue(actualRecord[key], value),
    )
  }
  return actual === expected
}

function pageContractProjection(value: unknown): unknown {
  if (!Array.isArray(value)) return null
  return value.map((item) => {
    const page = record(item)
    if (!page) return null
    return {
      id: page.id,
      route: page.route,
      title: page.title,
      blocks: page.blocks,
    }
  })
}

function blockContractProjection(value: unknown): unknown {
  if (!Array.isArray(value)) return null
  return value.map((item) => {
    const block = record(item)
    if (!block) return null
    return { name: block.name, type: block.type }
  })
}

export function findStudioOutcomeCatalogEntry(
  skuId: unknown,
): StudioOutcomeCatalogEntry | null {
  return STUDIO_OUTCOME_CATALOG.find((entry) => entry.skuId === skuId) ?? null
}

export function isStudioOutcomeSkuId(
  value: unknown,
): value is StudioOutcomeSkuId {
  return findStudioOutcomeCatalogEntry(value) !== null
}

export function parseStudioOutcomeIntent(value: unknown): StudioOutcomeIntent {
  const input = record(value)
  const entry = findStudioOutcomeCatalogEntry(input?.skuId)
  if (
    !input ||
    !exactKeys(input, ['source', 'skuId']) ||
    input.source !== STUDIO_FEATURED_OUTCOME_SOURCE ||
    !entry
  ) {
    throw new Error('studio_featured_outcome_intent_invalid')
  }
  return {
    source: STUDIO_FEATURED_OUTCOME_SOURCE,
    skuId: entry.skuId,
  }
}

export function createStudioOutcomeGenerationContract(
  skuId: StudioOutcomeSkuId,
): StudioOutcomeGenerationContract {
  switch (skuId) {
    case STUDIO_NEWSLETTER_MEMBERSHIP_SKU_ID:
      return {
        goldenSkuId: STUDIO_NEWSLETTER_MEMBERSHIP_GENERATION_GOLDEN_SKU_ID,
        pages: [
          {
            id: 'customer-membership',
            route: '/',
            title: 'Customer Membership',
            blocks: ['customer-membership-signup', 'customer-member-content'],
          },
          {
            id: 'operator-console',
            route: '/operator',
            title: 'Operator Console',
            blocks: [
              'operator-membership-overview',
              'operator-content-impact-controls',
            ],
          },
        ],
        blocks: [
          {
            name: 'customer-membership-signup',
            type: 'form',
          },
          {
            name: 'customer-member-content',
            type: 'list',
          },
          {
            name: 'operator-membership-overview',
            type: 'dashboard',
          },
          {
            name: 'operator-content-impact-controls',
            type: 'form',
          },
        ],
        globalState: {
          members: { type: 'array', default: [] },
          membershipDraft: { type: 'object', default: {} },
          currentMemberId: { type: 'string', default: '' },
          contentItems: { type: 'array', default: [] },
          welcomeJourney: { type: 'object', default: {} },
          operatorChangeDraft: { type: 'object', default: {} },
          changeImpact: { type: 'object', default: {} },
          publicationStatus: { type: 'string', default: 'draft' },
        },
      }
    case STUDIO_RESERVATION_OPERATIONS_SKU_ID:
      return {
        goldenSkuId: STUDIO_RESERVATION_OPERATIONS_GENERATION_GOLDEN_SKU_ID,
        pages: [
          {
            id: 'booking-operations',
            route: '/',
            title: 'Booking operations',
            blocks: ['booking-operations-regression'],
          },
        ],
        blocks: [
          {
            name: 'booking-operations-regression',
            type: 'form',
          },
        ],
        globalState: {
          availability: {
            type: 'object',
            default: {
              status: 'empty',
              date: '',
              venue: '',
              slot: '',
              flexible: false,
              alternativeDate: '',
              flexibleMessage: '',
              receipt: {
                receiptId: '',
                date: '',
                venue: '',
                slot: '',
              },
              message: 'Choose a date to begin',
            },
          },
          booking: {
            type: 'object',
            default: {
              status: 'idle',
              requestId: '',
              reservationId: '',
              confirmationAcknowledged: false,
              requestReceipt: {
                requestId: '',
                availabilityReceiptId: '',
                date: '',
                venue: '',
                slot: '',
              },
              message: 'No booking request submitted',
            },
          },
          bookingRequestIds: { type: 'array', default: [] },
          confirmedReservationIds: { type: 'array', default: [] },
          retryCount: { type: 'number', default: 0 },
          transitionLog: { type: 'array', default: [] },
        },
      }
  }
}

export function assertStudioOutcomeGenerationAuthority(
  value: StudioOutcomeGenerationAuthorityInput,
  skuId: StudioOutcomeSkuId,
): void {
  const contract = createStudioOutcomeGenerationContract(skuId)
  const appSpec = record(value.appSpec)
  const projectPlan = record(appSpec?.projectPlan)
  const adaptiveRuntime = record(appSpec?.adaptiveRuntime)
  const ontologyTrace = record(appSpec?.ontologyRetrievalTrace)
  const actionFlowTopology = record(appSpec?.actionFlowTopology)
  const actionFlowValidation = record(appSpec?.actionFlowValidationProof)
  const actionFlowBindingPlan = record(appSpec?.actionFlowBindingPlan)
  const actionFlowCompiledFlows = record(appSpec?.actionFlowCompiledFlows)
  const actionFlowCompiler = record(appSpec?.actionFlowCompilerProof)
  const actionFlowRuntime = record(appSpec?.actionFlowRuntimeSimulationProof)
  const actionFlowReadiness = record(appSpec?.actionFlowReadiness)
  const decomposePlanning = record(appSpec?.decomposePlanning)
  const errors: string[] = []

  if (appSpec?.goldenSkuId !== contract.goldenSkuId) {
    errors.push('appSpec.goldenSkuId')
  }
  if (
    !exactJsonValue(pageContractProjection(appSpec?.pages), contract.pages) ||
    !exactJsonValue(pageContractProjection(value.pages), contract.pages)
  ) {
    errors.push('pages')
  }
  if (
    !exactJsonValue(
      blockContractProjection(appSpec?.blocks),
      contract.blocks,
    ) ||
    !exactJsonValue(blockContractProjection(value.blocks), contract.blocks)
  ) {
    errors.push('blocks')
  }
  if (!exactJsonValue(appSpec?.globalState, contract.globalState)) {
    errors.push('appSpec.globalState')
  }
  if (
    !projectPlan ||
    !adaptiveRuntime ||
    !exactJsonValue(projectPlan.adaptiveRuntime, adaptiveRuntime) ||
    projectPlan.appClass !== appSpec?.appClass ||
    !exactJsonValue(projectPlan.runtimeTargets, appSpec?.runtimeTargets)
  ) {
    errors.push('appSpec.projectPlan')
  }
  if (!Array.isArray(appSpec?.datasources)) {
    errors.push('appSpec.datasources')
  }
  if (
    !ontologyTrace ||
    ontologyTrace.source !== 'decompose-api' ||
    ontologyTrace.readbackPassed !== true ||
    ontologyTrace.fallbackUsed !== false
  ) {
    errors.push('appSpec.ontologyRetrievalTrace')
  }
  if (
    !actionFlowTopology ||
    !actionFlowValidation ||
    actionFlowValidation.valid !== true ||
    !actionFlowBindingPlan ||
    !actionFlowCompiledFlows ||
    !actionFlowCompiler ||
    actionFlowCompiler.valid !== true ||
    !actionFlowRuntime ||
    actionFlowRuntime.valid !== true ||
    actionFlowRuntime.source !== 'decompose-api' ||
    !actionFlowReadiness ||
    actionFlowReadiness.status !== 'ready'
  ) {
    errors.push('appSpec.actionFlowAuthority')
  }

  if (errors.length > 0) {
    throw new Error(
      `studio_outcome_generation_authority_mismatch:${errors.join(',')}`,
    )
  }

  if (skuId === STUDIO_RESERVATION_OPERATIONS_SKU_ID) {
    // Reservation has no server-minted SKU-specific provenance assets in the
    // current live response. Exact shape alone cannot authorize lifecycle or
    // generation, so keep the outcome unavailable.
    throw new Error('studio_outcome_generation_authority_unavailable')
  }

  const expectedProjectPlan = {
    intent: appSpec?.description,
    outputKind: 'app',
    appClass: 'dynamic',
    runtimeTargets: ['web'],
    dynamicIslands: [],
    securityProfile: 'standard',
    performanceBudget: {
      firstPreviewP95Ms: 3000,
      warmRetrievalP95Ms: 800,
      evidenceCompleteP95Ms: 10000,
    },
    adaptiveRuntime: NEWSLETTER_MEMBERSHIP_ADAPTIVE_RUNTIME,
  }
  if (
    typeof appSpec?.description !== 'string' ||
    !appSpec.description.trim() ||
    appSpec.planningSource !== 'cogo-decompose-api' ||
    !exactJsonValue(projectPlan, expectedProjectPlan) ||
    !exactJsonValue(adaptiveRuntime, NEWSLETTER_MEMBERSHIP_ADAPTIVE_RUNTIME) ||
    !decomposePlanning ||
    decomposePlanning.planningSource !== 'cogo-decompose-api' ||
    decomposePlanning.status !== 'live' ||
    decomposePlanning.provider !== 'ontology-fast-path' ||
    decomposePlanning.model !== 'newsletter-membership-product' ||
    decomposePlanning.proxyContract !== 'appSpec'
  ) {
    throw new Error(
      'studio_outcome_generation_authority_mismatch:appSpec.newsletterProjectPlan',
    )
  }

  const selectedSubjects = Array.isArray(
    ontologyTrace?.selectedOntologySubjects,
  )
    ? ontologyTrace.selectedOntologySubjects
    : []
  const readbackSubjects = Array.isArray(ontologyTrace?.readbackSubjectIds)
    ? ontologyTrace.readbackSubjectIds
    : []
  const traceItems = Array.isArray(ontologyTrace?.items)
    ? ontologyTrace.items
    : []
  const expectedSubjects = [...NEWSLETTER_MEMBERSHIP_PROVENANCE_SUBJECTS].sort()
  const traceItemSubjects = traceItems.map((item) => {
    const traceItem = record(item)
    return typeof traceItem?.sourceRef === 'string' &&
      exactJsonValue(traceItem.evidenceRefs, [traceItem.sourceRef])
      ? traceItem.sourceRef
      : null
  })
  const hasExactProvenance =
    selectedSubjects.every((subject) => typeof subject === 'string') &&
    readbackSubjects.every((subject) => typeof subject === 'string') &&
    traceItemSubjects.every((subject) => subject !== null) &&
    exactJsonValue([...selectedSubjects].sort(), expectedSubjects) &&
    exactJsonValue([...readbackSubjects].sort(), expectedSubjects) &&
    exactJsonValue([...traceItemSubjects].sort(), expectedSubjects)
  const bindingBlockNames = Array.isArray(actionFlowBindingPlan?.bindings)
    ? [
        ...new Set(
          actionFlowBindingPlan.bindings.flatMap((binding) => {
            const event = record(record(binding)?.event)
            return typeof event?.blockName === 'string' ? [event.blockName] : []
          }),
        ),
      ].sort()
    : []
  const expectedBindingBlockNames = contract.blocks
    .map((block) => block.name)
    .sort()
  if (
    ontologyTrace?.selectedOntologySubject !==
      NEWSLETTER_MEMBERSHIP_PROVENANCE_SUBJECTS[0] ||
    !hasExactProvenance ||
    actionFlowTopology?.source !== 'decompose-api' ||
    actionFlowBindingPlan?.source !== 'decompose-api' ||
    !exactJsonValue(bindingBlockNames, expectedBindingBlockNames)
  ) {
    throw new Error(
      'studio_outcome_generation_authority_mismatch:appSpec.newsletterSkuProvenance',
    )
  }
}
